import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import CertificateCard from "../components/CertificateCard";
import { useLocale, useUiLabels } from "../context/LocaleContext";

const easeFluid = [0.32, 0.72, 0, 1] as const;

function CertificatesSection() {
  const { bundle } = useLocale();
  const ui = useUiLabels();
  const certificates = bundle.certificates;
  const filterAll = ui.certificates.filterAll;

  const [activeFilter, setActiveFilter] = useState<string>(filterAll);

  useEffect(() => {
    setActiveFilter(filterAll);
  }, [filterAll]);

  const categories = useMemo(() => {
    const cats = certificates.map((cert) => cert.category);
    return [filterAll, ...Array.from(new Set(cats))];
  }, [certificates, filterAll]);

  const filteredCertificates = useMemo(() => {
    if (activeFilter === filterAll) {
      return certificates.slice(0, 6);
    }
    return certificates.filter((cert) => cert.category === activeFilter).slice(0, 6);
  }, [activeFilter, certificates, filterAll]);

  const stats = useMemo(() => {
    const totalCerts = certificates.length;
    const organizations = Array.from(new Set(certificates.map((cert) => cert.organization))).length;
    const categoriesCount = Array.from(new Set(certificates.map((cert) => cert.category))).length;

    return { totalCerts, organizations, categoriesCount };
  }, [certificates]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: easeFluid }}
      className="flex w-full min-h-0 items-start justify-center px-4 py-24 md:px-8 md:py-32"
    >
      <div className="w-full max-w-6xl pb-16">
        <header className="mb-12 text-center md:mb-16">
          <p className="mb-4 inline-flex rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-violet-200/90 ring-1 ring-violet-400/25">
            {ui.certificates.badge}
          </p>
          <h1 className="text-4xl font-extrabold tracking-tight text-white md:text-5xl lg:text-6xl">{ui.certificates.title}</h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-zinc-400">{ui.certificates.intro}</p>
        </header>

        <div className="mb-10 grid grid-cols-3 gap-3 md:gap-5">
          {[
            { label: ui.certificates.statCerts, value: stats.totalCerts },
            { label: ui.certificates.statOrgs, value: stats.organizations },
            { label: ui.certificates.statAreas, value: stats.categoriesCount },
          ].map((s) => (
            <div key={s.label} className="bezel-outer text-center">
              <div className="bezel-inner px-3 py-5 md:py-6">
                <div className="text-2xl font-bold text-emerald-200 md:text-3xl">{s.value}</div>
                <div className="mt-1 text-[11px] font-medium uppercase tracking-wider text-zinc-500">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mb-10 flex flex-wrap justify-center gap-2">
          {categories.map((category) => (
            <motion.button
              key={category}
              type="button"
              whileTap={{ scale: 0.97 }}
              onClick={() => setActiveFilter(category)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-[transform,background-color,color] duration-700 ease-fluid ${
                activeFilter === category
                  ? "bg-gradient-to-r from-emerald-400/90 to-teal-500/90 text-[#050505]"
                  : "bg-white/[0.05] text-zinc-300 ring-1 ring-white/[0.08] hover:bg-white/[0.08]"
              }`}
            >
              {category}
            </motion.button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredCertificates.map((certificate, index) => (
            <motion.div
              key={`${certificate.title}-${index}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-20px" }}
              transition={{ duration: 0.75, delay: index * 0.05, ease: easeFluid }}
            >
              <CertificateCard
                cardId={`${certificate.title}-${index}`}
                title={certificate.title}
                description={certificate.description}
                imgUrl={certificate.imgUrl}
                category={certificate.category}
                organization={certificate.organization}
                date={certificate.date}
                credentialId={certificate.credentialId}
                verificationUrl={certificate.verificationUrl}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export default CertificatesSection;
